import React, { useState, useEffect, useCallback } from 'react';
import axios from 'axios';
import Layout from '../layout/Layout';

interface RewardSettings {
  coins_per_ad: number;
  coins_per_first_ad_daily: number;
  coins_per_referral_signup: number;
  coins_per_referral_verified: number;
  coins_per_referral_premium: number;
  coins_per_daily_login: number;
  welcome_bonus_coins: number;
  max_ads_per_day: number;
  max_referrals_per_day: number;
  ad_cooldown_seconds: number;
  coin_expiry_days: number | null;
}

interface SubscriptionPlan {
  id: number;
  name: string;
  duration_days: number;
  price_inr: string;
  coins_cost: number | null;
  is_active: boolean;
}

const RewardSettingsPage: React.FC = () => {
  const [settings, setSettings] = useState<RewardSettings | null>(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [profitability, setProfitability] = useState<any[]>([]);
  const [coinPlans, setCoinPlans] = useState<SubscriptionPlan[]>([]);

  useEffect(() => {
    loadSettings();
    loadCoinPlans();
  }, []);

  const loadSettings = async () => {
    setLoading(true);
    try {
      const response = await axios.get('/admin/reward-settings');
      if (response.data.success) {
        setSettings(response.data.data.settings);
      }
    } catch (error) {
      console.error('Error loading settings:', error);
    } finally {
      setLoading(false);
    }
  };

  const loadCoinPlans = async () => {
    try {
      const response = await axios.get('/admin/subscription-plans?status=active');
      if (response.data.success) {
        const plans = response.data.data.plans.filter((p: any) => p.coins_cost && p.coins_cost > 0);
        setCoinPlans(plans);
      }
    } catch (error) {
      console.error('Error loading coin plans:', error);
    }
  };

  const calculateProfitability = useCallback(() => {
    if (!settings || coinPlans.length === 0) return;
    
    const avgAdRevenue = 0.30;
    const results = coinPlans.map(plan => {
      const premiumValuePerDay = parseFloat(plan.price_inr) / plan.duration_days;
      const adsNeeded = Math.ceil((plan.coins_cost || 0) / settings.coins_per_ad);
      const revenueFromAds = adsNeeded * avgAdRevenue;
      const profit = revenueFromAds - premiumValuePerDay;
      const roi = premiumValuePerDay > 0 ? (profit / premiumValuePerDay) * 100 : 0;
      
      return {
        planName: plan.name,
        durationDays: plan.duration_days,
        coinsCost: plan.coins_cost,
        adsNeeded,
        revenueFromAds: revenueFromAds.toFixed(2),
        premiumCost: premiumValuePerDay.toFixed(2),
        profit: profit.toFixed(2),
        roi: roi.toFixed(0),
        isProfitable: profit > 0
      };
    });
    
    setProfitability(results);
  }, [settings, coinPlans]);

  useEffect(() => {
    if (settings) {
      calculateProfitability();
    }
  }, [settings, calculateProfitability]);

  const handleSave = async () => {
    if (!settings) return;

    setSaving(true);
    try {
      const response = await axios.put('/admin/reward-settings', settings);
      if (response.data.success) {
        alert('Reward settings updated successfully!');
        loadSettings();
      }
    } catch (error: any) {
      alert(error.response?.data?.message || 'Failed to update settings');
    } finally {
      setSaving(false);
    }
  };

  const updateSetting = (key: keyof RewardSettings, value: number | null) => {
    if (settings) {
      setSettings({ ...settings, [key]: value });
    }
  };

  if (loading || !settings) {
    return (
      <Layout>
        <div className="text-center py-5">
          <div className="spinner-border text-primary" role="status"></div>
        </div>
      </Layout>
    );
  }

  return (
    <Layout>
      <div className="row">
          <div className="col-12">
            <div className="page-title-box d-sm-flex align-items-center justify-content-between">
              <div>
                <h4 className="mb-1 font-size-24 fw-semibold" style={{ color: '#495057' }}>Reward Settings</h4>
                <p className="text-muted mb-0">Configure coin earning and spending rates</p>
              </div>
            </div>
          </div>
        </div>

        {/* Profitability Overview */}
        {profitability.length > 0 && (
          <div className="row mb-4">
            <div className="col-12">
              <div className="card">
                <div className="card-body">
                  <h5 className="card-title mb-3">
                    <i className="bx bx-line-chart me-2"></i>
                    Profitability Calculator (Live Plans)
                  </h5>
                  <div className="table-responsive">
                    <table className="table table-sm">
                      <thead>
                        <tr>
                          <th>Plan</th>
                          <th>Duration</th>
                          <th>Coins</th>
                          <th>Ads Needed</th>
                          <th>Ad Revenue</th>
                          <th>Premium Cost</th>
                          <th>Profit</th>
                          <th>ROI</th>
                        </tr>
                      </thead>
                      <tbody>
                        {profitability.map((calc, index) => (
                          <tr key={index} className={calc.isProfitable ? '' : 'table-warning'}>
                            <td><strong>{calc.planName}</strong></td>
                            <td>{calc.durationDays} days</td>
                            <td><span className="badge" style={{ backgroundColor: '#ff9800' }}>{calc.coinsCost} coins</span></td>
                            <td>{calc.adsNeeded} ads</td>
                            <td>₹{calc.revenueFromAds}</td>
                            <td>₹{calc.premiumCost}</td>
                            <td className={calc.isProfitable ? 'text-success' : 'text-danger'}>
                              <strong>₹{calc.profit}</strong>
                            </td>
                            <td className={calc.isProfitable ? 'text-success' : 'text-danger'}>
                              <strong>{calc.roi}%</strong> {calc.isProfitable ? '✅' : '⚠️'}
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  </div>
                  {profitability.some(p => !p.isProfitable) && (
                    <div className="alert alert-warning mt-3 mb-0">
                      <i className="bx bx-error-circle me-2"></i>
                      <strong>Warning:</strong> Some plans are not profitable. Consider adjusting coins_cost or coins_per_ad.
                    </div>
                  )}
                </div>
              </div>
            </div>
          </div>
        )}

        <div className="row">
          {/* Ad Rewards */}
          <div className="col-lg-6 mb-4">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title mb-4">
                  <i className="bx bx-video me-2"></i>
                  Ad Rewards
                </h5>
                
                <div className="mb-3">
                  <label className="form-label">Coins per Ad View</label>
                  <input
                    type="number"
                    className="form-control"
                    value={settings.coins_per_ad}
                    onChange={(e) => updateSetting('coins_per_ad', parseInt(e.target.value) || 0)}
                    min="1"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Coins for First Ad of Day (Bonus)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={settings.coins_per_first_ad_daily}
                    onChange={(e) => updateSetting('coins_per_first_ad_daily', parseInt(e.target.value) || 0)}
                    min="1"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Max Ads per Day</label>
                  <input
                    type="number"
                    className="form-control"
                    value={settings.max_ads_per_day}
                    onChange={(e) => updateSetting('max_ads_per_day', parseInt(e.target.value) || 0)}
                    min="1"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Ad Cooldown (seconds)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={settings.ad_cooldown_seconds}
                    onChange={(e) => updateSetting('ad_cooldown_seconds', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                  <small className="text-muted">Time user must wait between ads</small>
                </div>
              </div>
            </div>
          </div>

          {/* Referral Rewards */}
          <div className="col-lg-6 mb-4">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title mb-4">
                  <i className="bx bx-group me-2"></i>
                  Referral Rewards
                </h5>
                
                <div className="mb-3">
                  <label className="form-label">Friend Signs Up</label>
                  <input
                    type="number"
                    className="form-control"
                    value={settings.coins_per_referral_signup}
                    onChange={(e) => updateSetting('coins_per_referral_signup', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Friend Verifies Profile</label>
                  <input
                    type="number"
                    className="form-control"
                    value={settings.coins_per_referral_verified}
                    onChange={(e) => updateSetting('coins_per_referral_verified', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Friend Goes Premium</label>
                  <input
                    type="number"
                    className="form-control"
                    value={settings.coins_per_referral_premium}
                    onChange={(e) => updateSetting('coins_per_referral_premium', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Max Referrals per Day</label>
                  <input
                    type="number"
                    className="form-control"
                    value={settings.max_referrals_per_day}
                    onChange={(e) => updateSetting('max_referrals_per_day', parseInt(e.target.value) || 0)}
                    min="1"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* Other Rewards */}
          <div className="col-lg-6 mb-4">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title mb-4">
                  <i className="bx bx-gift me-2"></i>
                  Bonus Rewards
                </h5>
                
                <div className="mb-3">
                  <label className="form-label">Welcome Bonus (New Users)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={settings.welcome_bonus_coins}
                    onChange={(e) => updateSetting('welcome_bonus_coins', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                </div>

                <div className="mb-3">
                  <label className="form-label">Daily Login Bonus</label>
                  <input
                    type="number"
                    className="form-control"
                    value={settings.coins_per_daily_login}
                    onChange={(e) => updateSetting('coins_per_daily_login', parseInt(e.target.value) || 0)}
                    min="0"
                  />
                </div>
              </div>
            </div>
          </div>

          {/* System Settings */}
          <div className="col-lg-6 mb-4">
            <div className="card">
              <div className="card-body">
                <h5 className="card-title mb-4">
                  <i className="bx bx-cog me-2"></i>
                  System Settings
                </h5>
                
                <div className="mb-3">
                  <label className="form-label">Coin Expiry (days)</label>
                  <input
                    type="number"
                    className="form-control"
                    value={settings.coin_expiry_days ?? ''}
                    onChange={(e) => {
                      const value = e.target.value === '' ? null : parseInt(e.target.value) || null;
                      updateSetting('coin_expiry_days', value);
                    }}
                    min="1"
                    placeholder="Leave empty for no expiry"
                  />
                  <small className="text-muted">
                    {settings.coin_expiry_days 
                      ? `Earned coins expire after ${settings.coin_expiry_days} days` 
                      : 'Coins will never expire (leave empty)'}
                  </small>
                </div>
              </div>
            </div>
          </div>
        </div>

        {/* Save Button */}
        <div className="row">
          <div className="col-12">
            <button
              className="btn btn-primary btn-lg"
              onClick={handleSave}
              disabled={saving}
            >
              {saving ? (
                <>
                  <span className="spinner-border spinner-border-sm me-2"></span>
                  Saving...
                </>
              ) : (
                <>
                  <i className="bx bx-save me-2"></i>
                  Save Changes
                </>
              )}
            </button>
          </div>
        </div>
    </Layout>
  );
};

export default RewardSettingsPage;

